import { v4 as uuidv4 } from "uuid";

const cellKind = (cell: any) => cell?.radioType ?? cell?.unit ?? "";

/**
 * Pads rows that have fewer cells than headers when the shortfall is in a run of
 * dynamic columns (e.g. header-only VHF columns). New cells are blank clones of the
 * run's last cell. Never removes cells; skips nested headers and subrows.
 */
export const alignRowsToHeaders = (section: {
  headers?: any[];
  rows?: any[];
}) => {
  const headers = section?.headers;
  if (!Array.isArray(headers) || !Array.isArray(section.rows)) return section;
  if (headers.some(h => h?.cols) || !headers.some(h => h?.dynamicCols)) {
    return section;
  }

  section.rows.forEach(row => {
    if (!row || row.subrows || !row.cols || Array.isArray(row.cols)) return;

    const entries: [string, any][] = Object.entries(row.cols);
    let deficit = headers.length - entries.length;
    if (deficit <= 0) return;

    const result: [string, any][] = [];
    let p = 0;
    let i = 0;
    while (i < headers.length && p < entries.length) {
      const header = headers[i];
      if (!header.dynamicCols) {
        result.push(entries[p++]);
        i++;
        continue;
      }

      let n = 1;
      while (
        i + n < headers.length &&
        headers[i + n].dynamicCols &&
        headers[i + n].name === header.name
      ) {
        n++;
      }

      const kind = cellKind(entries[p][1]);
      let k = 0;
      while (k < n && p + k < entries.length && cellKind(entries[p + k][1]) === kind) {
        k++;
      }
      for (let c = 0; c < k; c++) result.push(entries[p + c]);

      const missing = Math.min(n - k, deficit);
      for (let c = 0; c < missing; c++) {
        const clone = JSON.parse(JSON.stringify(entries[p + k - 1][1]));
        clone.value = "";
        result.push([uuidv4(), clone]);
      }
      deficit -= missing;
      p += k;
      i += n;
    }
    while (p < entries.length) result.push(entries[p++]);

    row.cols = Object.fromEntries(result);
  });

  return section;
};
